import { PaymentType } from "src/api";
import { useDepositWithdrawContext } from "src/contexts";
import { _t } from "src/utils/translation";

// One full sentence per payment type — a translated noun dropped into a
// shared template breaks grammar in pt/ar (definiteness, agreement).
const LIMIT_EXCEEDED_MESSAGE: Record<PaymentType, { key: string; defaultValue: string }> = {
  [PaymentType.Deposit]: {
    key: "VALIDATION.DEPOSIT_LIMIT_EXCEEDED",
    defaultValue: "Amount exceeds your available deposit limit ({{available}} {{currency}})",
  },
  [PaymentType.Withdrawal]: {
    key: "VALIDATION.WITHDRAWAL_LIMIT_EXCEEDED",
    defaultValue: "Amount exceeds your available withdrawal limit ({{available}} {{currency}})",
  },
};

const MINIMUM_AMOUNT_MESSAGE: Record<PaymentType, { key: string; defaultValue: string }> = {
  [PaymentType.Deposit]: {
    key: "VALIDATION.MINIMUM_DEPOSIT_AMOUNT",
    defaultValue: "Minimum deposit amount is {{minAmount}} {{currency}}",
  },
  [PaymentType.Withdrawal]: {
    key: "VALIDATION.MINIMUM_WITHDRAWAL_AMOUNT",
    defaultValue: "Minimum withdrawal amount is {{minAmount}} {{currency}}",
  },
};

interface UsePaymentValidationProps {
  amount: number;
  paymentType: PaymentType;
  fee?: number;
  /**
   * Tightest remaining headroom across the user's limit-group periods
   * (in wallet currency), from `useCalculatedLimits`. `Infinity`/undefined
   * means no cap configured — no limit error is raised.
   */
  availableLimit?: number;
}

export const usePaymentValidation = ({
  amount,
  paymentType,
  fee = 0,
  availableLimit = Infinity,
}: UsePaymentValidationProps) => {
  const { paymentsLimits, balance } = useDepositWithdrawContext();
  const {
    currency: { currency_id },
    free_balance,
  } = balance;

  const minAmount =
    paymentType === PaymentType.Deposit ? paymentsLimits.deposit_min_amount : paymentsLimits.withdrawal_min_amount;
  const minAmountError = amount && amount < minAmount;

  const notEnoughBalance = paymentType === PaymentType.Withdrawal && amount && free_balance < amount;

  const amountAfterFeeError = amount && fee && amount - fee <= 0;

  // Block when the amount exceeds the user's remaining limit-group headroom.
  const limitExceeded = Number.isFinite(availableLimit) && amount && amount > availableLimit;

  const paymentValidationErrors = [
    limitExceeded
      ? [
          _t(LIMIT_EXCEEDED_MESSAGE[paymentType].key, {
            defaultValue: LIMIT_EXCEEDED_MESSAGE[paymentType].defaultValue,
            available: availableLimit,
            currency: currency_id,
          }),
        ]
      : [],
    minAmountError
      ? [
          _t(MINIMUM_AMOUNT_MESSAGE[paymentType].key, {
            defaultValue: MINIMUM_AMOUNT_MESSAGE[paymentType].defaultValue,
            minAmount,
            currency: currency_id,
          }),
        ]
      : [],
    notEnoughBalance
      ? [
          _t("VALIDATION.NOT_ENOUGH_BALANCE", {
            balance: free_balance,
          }),
        ]
      : [],
    amountAfterFeeError
      ? [
          _t("VALIDATION.AMOUNT_AFTER_FEE_SHOULD_BE_GREATER_THAN_ZERO"),
        ]
      : [],
  ].flat();

  return {
    paymentValidationErrors,
  };
};
